import { AttributeDto } from "api"
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react"

interface UseUrlFiltersProps {
    initialAttributes?: AttributeDto[]
    searchTerm?: string
    availableAttributes?: AttributeDto[] // Add this to reconstruct full objects from IDs
}

interface UseUrlFiltersReturn {
    selectedAttributes: AttributeDto[]
    setSelectedAttributes: (attributes: AttributeDto[] | ((prev: AttributeDto[]) => AttributeDto[])) => void
    searchTerm: string
    setSearchTerm: Dispatch<SetStateAction<string>>
    updateUrlParams: () => void
}

export const useUrlFilters = ({
    initialAttributes = [],
    searchTerm: initialSearchTerm = "",
    availableAttributes = []
}: UseUrlFiltersProps = {}): UseUrlFiltersReturn => {
    const [selectedAttributes, setSelectedAttributesState] = useState<AttributeDto[]>(initialAttributes)
    const [searchTerm, setSearchTermState] = useState<string>(initialSearchTerm)
    const [hasInitialized, setHasInitialized] = useState(false)
    const [savedFiltersParam, setSavedFiltersParam] = useState<string | null>(null)

    // Parse URL parameters only once on mount and save them
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search)

        // Get search term from URL
        const searchParam = urlParams.get('search')
        if (searchParam) {
            setSearchTermState(decodeURIComponent(searchParam))
        }

        // Save filters parameter for later processing
        const filtersParam = urlParams.get('filters')
        if (filtersParam) {
            setSavedFiltersParam(filtersParam)
        }

        setHasInitialized(true)
    }, []) // Empty dependency array - only run once

    // Parse filters from URL when availableAttributes become available
    useEffect(() => {
        if (availableAttributes.length > 0 && hasInitialized && savedFiltersParam) {
            // Decode the saved filters parameter
            const decodedFiltersParam = decodeURIComponent(savedFiltersParam)
            const attributeIds = decodedFiltersParam.split(',').filter(id => id.trim())
            const reconstructedAttributes = attributeIds
                .map(id => availableAttributes.find(attr => attr.Name === id))
                .filter(attr => attr !== undefined) as AttributeDto[]
            setSelectedAttributesState(reconstructedAttributes)
            // Clear the saved param after processing
            setSavedFiltersParam(null)

        }
    }, [availableAttributes, hasInitialized, savedFiltersParam])

    // Update URL when filters or search term change
    const updateUrlParams = useCallback(() => {
        if (!hasInitialized) {
            return
        } // Don't update URL until initialized

        const url = new URL(window.location.href)
        const params = new URLSearchParams(url.search)

        // Update filters parameter - only store attribute IDs
        if (selectedAttributes.length > 0) {
            const attributeIds = selectedAttributes.map(attr => attr.Name).join(',')
            params.set('filters', attributeIds)
        } else {
            params.delete('filters')
        }

        // Update search parameter
        if (searchTerm.trim()) {
            params.set('search', encodeURIComponent(searchTerm))
        } else {
            params.delete('search')
        }

        // Fix URL construction - query params should come before hash
        const queryString = params.toString()
        const newUrl = `${url.pathname}${queryString ? `?${queryString}` : ''}${url.hash}`
        window.history.replaceState(null, '', newUrl)
    }, [selectedAttributes, searchTerm, hasInitialized])

    // Add effect to update URL when search term or attributes change
    useEffect(() => {
        updateUrlParams()
    }, [searchTerm, selectedAttributes])


    // Remove the auto-update effect that was causing the loop
    // URL will be updated manually when needed

    // Custom setter for attributes
    const setSelectedAttributes = useCallback((
        attributes: AttributeDto[] | ((prev: AttributeDto[]) => AttributeDto[])
    ) => {
        setSelectedAttributesState(prev => {
            const newAttributes = typeof attributes === 'function' ? attributes(prev) : attributes
            return newAttributes
        })
    }, [])

    // Custom setter for search term that updates URL
    const setSearchTerm = useCallback((term: string | ((prev: string) => string)) => {
        setSearchTermState(prev => {
            const newTerm = typeof term === 'function' ? term(prev) : term
            return newTerm
        })
    }, [])

    return {
        selectedAttributes,
        setSelectedAttributes,
        searchTerm,
        setSearchTerm,
        updateUrlParams
    }
}
